import { Extrapolation, interpolate, useAnimatedStyle, useDerivedValue, useSharedValue, withSpring } from 'react-native-reanimated';
import { useEffect, useRef } from 'react';
import { ThumbnailItemProps } from '@v2/components/imageHandling/components/SmallSlider/ThumbnailItem';
import { scale as initialScale } from '@components/frame';
import { ACTIVE_THUMBNAIL_HEIGHT, ACTIVE_THUMBNAIL_WIDTH, THUMBNAIL_HEIGHT, THUMBNAIL_WIDTH } from '@v2/components/imageHandling/constants';
import { StyleSheet } from 'react-native';

interface UseThumbnailItemCalculationsParams extends Pick<ThumbnailItemProps, 'index' | 'selectedIndex' | 'image' | 'scrollProgress' | 'scale'> {}

export const useThumbnailItemCalculations = ({
  image,
  index,
  selectedIndex,
  scrollProgress,
  scale = initialScale,
}: UseThumbnailItemCalculationsParams) => {
  const isActive = index === selectedIndex;
  const animatedScale = useSharedValue(0);
  const isFirstRender = useRef(true);

  useDerivedValue(() => {
    if (scrollProgress) {
      const currentScrollIndex = Math.round(scrollProgress.value);
      const isCurrentlyActive = currentScrollIndex === index;
      animatedScale.value = withSpring(isCurrentlyActive ? 1 : 0, {
        damping: 15,
        stiffness: 150,
      });
    }
  });

  useEffect(() => {
    if (!scrollProgress) {
      if (isFirstRender.current) {
        isFirstRender.current = false;
        animatedScale.value = isActive ? 1 : 0;
        return;
      }
      animatedScale.value = withSpring(isActive ? 1 : 0, {
        damping: 15,
        stiffness: 150,
      });
    }
  }, [isActive, selectedIndex, scrollProgress]);

  const animatedStyle = useAnimatedStyle(() => {
    const borderWidth = interpolate(animatedScale.value, [0, 1], [0, 2], Extrapolation.CLAMP);
    const opacity = interpolate(animatedScale.value, [0, 1], [0.7, 1], Extrapolation.CLAMP);

    const height = interpolate(
      animatedScale.value,
      [0, 1],
      [scale(THUMBNAIL_HEIGHT - StyleSheet.hairlineWidth, false), scale(ACTIVE_THUMBNAIL_HEIGHT, false)],
      Extrapolation.CLAMP
    );
    const width = interpolate(
      animatedScale.value,
      [0, 1],
      [scale(THUMBNAIL_WIDTH, false), scale(ACTIVE_THUMBNAIL_WIDTH, false)],
      Extrapolation.CLAMP
    );

    return { width, height, borderWidth, opacity };
  });

  const imageSource = image.urlSmall || image.url;

  return { imageSource, animatedStyle, isActive };
};
